import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createTenantCustomerApi,
  deleteTenantCustomerUploadApi,
  getTenantCustomerApi,
  getTenantCustomerByCodeApi,
  getTenantCustomersApi,
  updateTenantCustomerApi,
  uploadTenantCustomerFilesApi,
} from "../../api/common/CustomerApi";

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Something went wrong.";

const unwrapResponse = (payload) => payload?.response ?? payload;

const initialState = {
  customer: null,
  customers: [],
  customerByCode: null,
  uploadResponse: null,
  page: 0,
  size: 100,
  sortBy: "customerId",
  sortDir: "desc",
  loading: false,
  saving: false,
  uploading: false,
  deletingUpload: false,
  error: null,
};

const createCustomerThunk = (type, api) =>
  createAsyncThunk(type, async (payload, { rejectWithValue }) => {
    try {
      const response = await api(payload);
      return response?.data;
    } catch (error) {
      return rejectWithValue({ errorMessage: getErrorMessage(error) });
    }
  });

export const getTenantCustomer = createCustomerThunk(
  "commonCustomer/getTenantCustomer",
  getTenantCustomerApi
);
export const getTenantCustomers = createCustomerThunk(
  "commonCustomer/getTenantCustomers",
  getTenantCustomersApi
);
export const getTenantCustomerByCode = createCustomerThunk(
  "commonCustomer/getTenantCustomerByCode",
  getTenantCustomerByCodeApi
);
export const createTenantCustomer = createCustomerThunk(
  "commonCustomer/createTenantCustomer",
  createTenantCustomerApi
);
export const updateTenantCustomer = createCustomerThunk(
  "commonCustomer/updateTenantCustomer",
  updateTenantCustomerApi
);
export const uploadTenantCustomerFiles = createCustomerThunk(
  "commonCustomer/uploadTenantCustomerFiles",
  uploadTenantCustomerFilesApi
);
export const deleteTenantCustomerUpload = createCustomerThunk(
  "commonCustomer/deleteTenantCustomerUpload",
  deleteTenantCustomerUploadApi
);

const customerSlice = createSlice({
  name: "commonCustomer",
  initialState,
  reducers: {
    clearTenantCustomer: (state) => {
      state.customer = null;
      state.customerByCode = null;
      state.error = null;
    },
    setTenantCustomerPagination: (state, action) => {
      const { page, size, sortBy, sortDir } = action.payload || {};
      if (page !== undefined) state.page = page;
      if (size !== undefined) state.size = size;
      if (sortBy !== undefined) state.sortBy = sortBy;
      if (sortDir !== undefined) state.sortDir = sortDir;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTenantCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTenantCustomer.fulfilled, (state, action) => {
        state.customer = unwrapResponse(action.payload);
        state.loading = false;
      })
      .addCase(getTenantCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.errorMessage;
      })
      .addCase(getTenantCustomers.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        const params = action.meta.arg || {};
        state.page = params.page ?? state.page;
        state.size = params.size ?? state.size;
        state.sortBy = params.sortBy ?? state.sortBy;
        state.sortDir = params.sortDir ?? state.sortDir;
      })
      .addCase(getTenantCustomers.fulfilled, (state, action) => {
        state.customers = unwrapResponse(action.payload);
        state.loading = false;
      })
      .addCase(getTenantCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.errorMessage;
      })
      .addCase(getTenantCustomerByCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTenantCustomerByCode.fulfilled, (state, action) => {
        state.customerByCode = unwrapResponse(action.payload);
        state.loading = false;
      })
      .addCase(getTenantCustomerByCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.errorMessage;
      });

    [createTenantCustomer, updateTenantCustomer].forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.saving = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.customer = unwrapResponse(action.payload);
          state.saving = false;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.saving = false;
          state.error = action.payload?.errorMessage;
        });
    });

    builder
      .addCase(uploadTenantCustomerFiles.pending, (state) => {
        state.uploading = true;
        state.uploadResponse = null;
        state.error = null;
      })
      .addCase(uploadTenantCustomerFiles.fulfilled, (state, action) => {
        state.uploadResponse = unwrapResponse(action.payload);
        state.uploading = false;
      })
      .addCase(uploadTenantCustomerFiles.rejected, (state, action) => {
        state.uploading = false;
        state.error = action.payload?.errorMessage;
      })
      .addCase(deleteTenantCustomerUpload.pending, (state) => {
        state.deletingUpload = true;
        state.error = null;
      })
      .addCase(deleteTenantCustomerUpload.fulfilled, (state) => {
        state.deletingUpload = false;
      })
      .addCase(deleteTenantCustomerUpload.rejected, (state, action) => {
        state.deletingUpload = false;
        state.error = action.payload?.errorMessage;
      });
  },
});

export const { clearTenantCustomer, setTenantCustomerPagination } =
  customerSlice.actions;
export default customerSlice.reducer;
